import { z } from 'zod';
import { SortQuerySchema } from '../common';

const moneyRegex = /^\d+(\.\d{1,2})?$/;
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

export const CreatePagoDtoSchema = z
  .object({
    idCliente: z.coerce.number().int(),
    idTipoPago: z.coerce.number().int(),
    valor: z.string().regex(moneyRegex, 'valor must be a valid money value (max 2 decimals)'),
    latitud: z.coerce.number().optional(),
    longitud: z.coerce.number().optional(),
    comentario: z.string().optional(),
    fechaCobro: z.string().optional(),
    nombreCliente: z.string().optional(),
    // Billing core (Fase B) — prepago (modalidad ADELANTADO). 'NORMAL' is the
    // historical behavior and the default, keeping the existing flow intact.
    modalidad: z.enum(['NORMAL', 'ADELANTADO']).default('NORMAL'),
    mesesAdelantado: z.coerce.number().int().min(1).max(12).optional(),
    descuentoTipo: z.enum(['MONTO', 'PORCENTAJE']).nullable().optional(),
    descuentoValor: z.coerce.number().min(0).optional(),
    totalPlanificado: z
      .string()
      .regex(moneyRegex, 'totalPlanificado must be a valid money value (max 2 decimals)')
      .optional(),
    /** Snapshot of the backend candidate shown by the prepaid modal. */
    idFacturaActual: z.coerce.number().int().positive().nullable().optional(),
    /** Explicitly replace the current-period invoice when the operator keeps the checkbox on. */
    reemplazarFacturaActual: z.boolean().optional(),
    /**
     * Targeted one-off payment (single invoice): when present the payment is
     * applied ONLY to that invoice, never through FIFO. Kept for compatibility
     * with existing clients; `idFacturasPago` is the multi-invoice form and
     * sending both fields at once is rejected.
     */
    idFacturaPago: z.coerce.number().int().positive().nullable().optional(),
    /**
     * Targeted multi-invoice payment: the amount is applied ONLY to the named
     * invoices, oldest period first, capped by the sum of their remainders.
     * An empty list is rejected: naming zero invoices is not a target, it is a
     * missing choice.
     */
    idFacturasPago: z.array(z.coerce.number().int().positive()).optional(),
    // Boleta (deposito/transferencia) — Fase E2: candidatos OCR que el
    // operador confirma/corrige antes de crear el pago (human-in-the-loop).
    // archivoBoleta es el nombre del archivo devuelto por POST /pagos/boleta/ocr.
    referenciaBoleta: z.string().optional(),
    montoBoleta: z.string().optional(),
    fechaBoleta: z.string().optional(),
    archivoBoleta: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.modalidad === 'ADELANTADO' && val.mesesAdelantado === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mesesAdelantado'],
        message: 'mesesAdelantado (1-12) is required when modalidad is ADELANTADO',
      });
    }
    if (Number(val.valor) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valor'],
        message: 'valor must be greater than 0',
      });
    }
    if (val.idFacturasPago !== undefined && val.idFacturasPago.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['idFacturasPago'],
        message: 'idFacturasPago must name at least one invoice',
      });
    }
    if (val.idFacturasPago !== undefined && val.idFacturaPago != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['idFacturasPago'],
        message: 'send either idFacturaPago (single) or idFacturasPago (list), never both',
      });
    }
  });

export type CreatePagoDto = z.infer<typeof CreatePagoDtoSchema>;

export const AnularPagoDtoSchema = z.object({
  motivo: z.string().min(1),
});

export type AnularPagoDto = z.infer<typeof AnularPagoDtoSchema>;

export const QueryPagosDtoSchema = z
  .object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    idCliente: z.coerce.number().int().optional(),
    // Business calendar dates: the backend resolves these to America/Guatemala
    // business-midnight instants on the timestamptz p.fecha_creacion column.
    fechaDesde: z.string().regex(isoDateRegex, 'fechaDesde must be a YYYY-MM-DD date').optional(),
    fechaHasta: z.string().regex(isoDateRegex, 'fechaHasta must be a YYYY-MM-DD date').optional(),
    idEstadoPago: z.coerce.number().int().optional(),
    idTipoPago: z.coerce.number().int().optional(),
    // Registering system user (pago_cliente.id_usuario, uuid); filters list + stats.
    idUsuario: z.string().uuid().optional(),
  })
  .merge(SortQuerySchema);

export type QueryPagosDto = z.infer<typeof QueryPagosDtoSchema>;
